<template>
  <div class="p-3">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="mb-0">CF Enterprise Bulk Automation</h5>
      <button class="btn btn-primary" :disabled="batchCreating" @click="initiateBulkEnterprise">
        <i class="fa fa-bolt mr-1"></i>
        <span v-if="batchCreating"><i class="fa fa-spinner fa-spin mr-1"></i> Creating Batch...</span>
        <span v-else>Initiate CF Enterprise in Bulk</span>
      </button>
    </div>
    <p class="text-muted mb-3">Select how many pending CF Enterprise tasks to queue into a new automation batch.</p>

    <ul class="nav nav-tabs mb-3">
      <li class="nav-item" v-for="tab in statusTabs" :key="tab.key">
        <a
          href="#"
          class="nav-link"
          :class="{ active: statusFilter === tab.key }"
          @click.prevent="setStatusFilter(tab.key)"
        >{{ tab.label }}</a>
      </li>
    </ul>

    <div v-if="loadingBatches && batches.length === 0" class="text-center py-4">
      <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
    </div>
    <div v-else-if="batches.length === 0" class="alert alert-light text-muted text-center">
      No {{ statusTabs.find(t => t.key === statusFilter).label.toLowerCase() }} batches found.
    </div>
    <div v-else class="table-responsive">
      <table class="table table-bordered table-hover">
        <thead class="thead-light">
          <tr>
            <th>Batch ID</th>
            <th>Status</th>
            <th>Total</th>
            <th>Created At</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="batch in batches" :key="batch.id">
            <td style="font-family:monospace;font-size:12px;">{{ batch.batchId }}</td>
            <td>
              <span :style="batchStatusStyle(batch.status)" style="border-radius:99px;padding:3px 10px;font-size:11px;font-weight:600;display:inline-block;">
                {{ batchStatusLabel(batch.status) }}
              </span>
            </td>
            <td>{{ batch.totalCount }}</td>
            <td>{{ formatDate(batch.createdAt) }}</td>
            <td class="text-center">
              <button class="btn btn-sm btn-outline-secondary" @click="$router.push({ name: 'cfEnterpriseBatchDetail', params: { batchId: batch.batchId } })">
                <i class="fa fa-list mr-1"></i> View Domains
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="d-flex align-items-center justify-content-between mt-3" v-if="currentPage > 1 || hasNextPage">
        <small class="text-muted">Page {{ currentPage }}</small>
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 || loadingBatches }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">&laquo; Prev</a>
          </li>
          <li class="page-item disabled">
            <span class="page-link">{{ currentPage }}</span>
          </li>
          <li class="page-item" :class="{ disabled: !hasNextPage || loadingBatches }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next &raquo;</a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import {db} from '@/firebase/init';
import swal from 'sweetalert2';

export default {
  name: 'CFProvisioning',
  data() {
    return {
      batchCreating: false,
      loadingBatches: false,
      batches: [],
      allBatches: [],
      currentPage: 1,
      pageSize: 10,
      hasNextPage: false,
      unsubscribe: null,
      statusFilter: 'pending',
      statusTabs: [
        {
          key: 'pending', label: 'Pending', icon: 'fa fa-clock-o',
          activeStyle: 'background:#fff8e1;color:#f57f17;border-color:#f9a825;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        },
        {
          key: 'inProgress', label: 'In Progress', icon: 'fa fa-spinner fa-spin',
          activeStyle: 'background:#e3f2fd;color:#1565c0;border-color:#90caf9;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        },
        {
          key: 'completed', label: 'Completed', icon: 'fa fa-check-circle',
          activeStyle: 'background:#e8f5e9;color:#2e7d32;border-color:#a5d6a7;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        },
        {
          key: 'failed', label: 'Failed', icon: 'fa fa-times-circle',
          activeStyle: 'background:#fdecea;color:#c62828;border-color:#ef9a9a;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        }
      ]
    };
  },
  mounted() {
    this.listenBatches();
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  computed: {
    hasActiveBatch() {
      const done = ['complete', 'completed', 'failed', 'fail'];
      return this.batches.some(b => !done.includes(b.status));
    }
  },
  methods: {
    listenBatches() {
      if (this.unsubscribe) this.unsubscribe();
      this.loadingBatches = true;
      const statusValues = {
        pending: ['pending'],
        inProgress: ['running', 'in-progress', 'inProgress'],
        completed: ['complete', 'completed', 'success'],
        failed: ['failed', 'fail']
      };
      let query = db.collection('cfEnterpriseBatches')
        .where('status', 'in', statusValues[this.statusFilter]);
      query = query.limit(500);
      this.unsubscribe = query.onSnapshot(snapshot => {
        const sorted = snapshot.docs.slice().sort((a, b) => {
          const aT = a.data().createdAt && a.data().createdAt.toDate ? a.data().createdAt.toDate() : new Date(a.data().createdAt || 0);
          const bT = b.data().createdAt && b.data().createdAt.toDate ? b.data().createdAt.toDate() : new Date(b.data().createdAt || 0);
          return bT - aT;
        });
        this.allBatches = sorted.map(doc => ({ id: doc.id, ...doc.data() }));
        this.applyPage();
        this.loadingBatches = false;
      }, err => {
        console.error('listenBatches error', err);
        this.loadingBatches = false;
      });
    },
    applyPage() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      this.batches = this.allBatches.slice(start, end);
      this.hasNextPage = this.allBatches.length > end;
    },
    setStatusFilter(key) {
      if (this.statusFilter === key) return;
      this.statusFilter = key;
      this.currentPage = 1;
      this.listenBatches();
    },
    goToPage(p) {
      if (p === this.currentPage) return;
      if (p < 1) return;
      if (p > this.currentPage && !this.hasNextPage) return;
      this.currentPage = p;
      this.applyPage();
    },
    statusBadge(status) {
      return {
        'badge-warning': status === 'pending',
        'badge-info': status === 'running' || status === 'in-progress' || status === 'inProgress',
        'badge-success': status === 'complete' || status === 'completed',
        'badge-danger': status === 'failed' || status === 'fail'
      };
    },
    statusLabel(status) {
      const map = {
        'pending': 'Pending',
        'running': 'Running',
        'in-progress': 'In Progress',
        'inProgress': 'In Progress',
        'complete': 'Complete',
        'completed': 'Complete',
        'failed': 'Failed',
        'fail': 'Failed'
      };
      return map[status] || status;
    },
    batchStatusLabel(status) {
      const map = {
        pending: 'Pending', running: 'In Progress', 'in-progress': 'In Progress',
        inProgress: 'In Progress', complete: 'Completed', completed: 'Completed',
        success: 'Completed', failed: 'Failed', fail: 'Failed'
      };
      return map[status] || status || 'Pending';
    },
    batchStatusStyle(status) {
      if (status === 'complete' || status === 'completed' || status === 'success') return 'background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7;';
      if (status === 'failed' || status === 'fail') return 'background:#fdecea;color:#c62828;border:1px solid #ef9a9a;';
      if (status === 'running' || status === 'in-progress' || status === 'inProgress') return 'background:#e3f2fd;color:#1565c0;border:1px solid #90caf9;';
      return 'background:#f5f5f5;color:#757575;border:1px solid #e0e0e0;';
    },
    domainCounts(batch) {
      const counts = { success: 0, failed: 0, inProgress: 0, pending: 0, followUp: 0 };
      if (!batch.domains) return counts;
      batch.domains.forEach(function(d) {
        const s = d.status || '';
        if (s === 'follow-up') { counts.followUp++; return; }
        if (s === 'pass' || s === 'complete' || s === 'completed' || s === 'success') counts.success++;
        else if (s === 'fail' || s === 'failed' || s === 'error') counts.failed++;
        else if (s === 'running' || s === 'in-progress' || s === 'inProgress') counts.inProgress++;
        else counts.pending++;
      });
      return counts;
    },
    formatDate(val) {
      if (!val) return '—';
      const d = val.toDate ? val.toDate() : new Date(val);
      return d.toLocaleString();
    },
    async initiateBulkEnterprise() {
      const result = await swal({
        title: 'Bulk CF Enterprise Automation',
        html: `
          <p class="mb-2">How many pending tasks do you want to automate?</p>
          <input id="swal-batch-count" type="number" min="1" max="100" value="5"
            class="swal2-input" style="width:120px;text-align:center;" />
        `,
        showCancelButton: true,
        confirmButtonText: 'Create Batch',
        preConfirm: () => {
          const val = parseInt(document.getElementById('swal-batch-count').value, 10);
          if (!val || val < 1 || val > 100) {
            swal.showValidationMessage('Please enter a number between 1 and 100');
            return false;
          }
          return val;
        }
      });

      const count = result.value;
      if (!count) return;

      try {
        this.batchCreating = true;

        const domains = [];
        let lastDoc = null;

        while (domains.length < count) {
          let query = db.collection('domains')
            .where('cfEnterprise.status', '==', 'pending')
            .limit(count);
          if (lastDoc) query = query.startAfter(lastDoc);

          const snapshot = await query.get();
          if (snapshot.empty) break;

          for (let i = 0; i < snapshot.docs.length; i++) {
            if (domains.length >= count) break;
            const doc = snapshot.docs[i];
            const data = doc.data();
            const cfEnt = data.cfEnterprise || {};
            if (cfEnt.checkinBy) continue;
            if (cfEnt.followUp === true) continue;
            domains.push({
              ip: data.ip || '',
              id: doc.id,
              uid: data.uid || '',
              domain: data.domain || '',
              status: 'pending'
            });
          }

          lastDoc = snapshot.docs[snapshot.docs.length - 1];
          if (snapshot.docs.length < count) break;
        }

        if (!domains.length) {
          return swal('No Pending Tasks', 'There are no pending CF Enterprise tasks to queue.', 'info');
        }
        console.log(domains)
        // return
        const batchRef = db.collection('cfEnterpriseBatches').doc();
        await batchRef.set({
          batchId: batchRef.id,
          status: 'pending',
          createdAt: new Date(),
          totalCount: domains.length,
          domains
        });

        await this.$http.post('zone/batch-automate/run', {
          grant_type: 'token',
          batchId: batchRef.id
        });
      } catch (e) {
        console.error('initiateBulkEnterprise error', e);
        swal('Error', 'Something went wrong while creating the batch.', 'error');
      } finally {
        this.batchCreating = false;
      }
    }
  }
};
</script>
<style>
.nav-tabs .nav-item .nav-link.active:before,
.nav-tabs .nav-item .nav-link.active:hover:before,
.nav-tabs .nav-item .nav-link.active:focus:before {
  border: 0;
}

.nav-tabs {
  border-bottom: 1px solid #dee2e6;
}

.nav-tabs .nav-link {
  border: 1px solid transparent !important;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.nav-tabs .nav-item.show .nav-link,
.nav-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff !important;
}

.nav-tabs .nav-item .nav-link.active {
  color: #5b7cfb;
}
</style>
